import { Save } from '@mui/icons-material';
import {
  Divider,
  Fade,
  Grid,
  MenuItem,
  Typography,
  Box,
  Button,
  TextField,
  Autocomplete
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useContext, useEffect, useState, useMemo } from 'react';
// Components
import { Modal, Loader, SMBSelect } from '../../../components';
import { UserContext } from '../../../context';
import {
  getPlaceOrderAvailableReportsTo,
  getAccessInformation,
  getAllAppRoles,
  getUserAppRoles,
  updateUserRoles
} from '../../../fetch';
import { isAllowedToBeEditedOrDeleted, alphaSort } from '../../../helpers';
import {
  IAccessInformation,
  IUserWithAccess,
  IDropdownResponse,
  IManagePlaceOrderReportsTo
} from '../../../models';
import { AccordionList } from '../../../components/accordion/simple-accordion-list';
import { getAdminLookupPlaceOrderRoles } from '../../../fetch/lookups';

interface IEntraInternalUserModal {
  open: boolean;
  onClose: (shouldUpdate?: boolean) => void;
  currentUser?: IUserWithAccess;
  fetchUsers?: () => void;
}

export const EntraInternalUserModal: FC<IEntraInternalUserModal> = ({
  open,
  onClose,
  currentUser,
  fetchUsers
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, loadUser } = useContext(UserContext);
  const [selectedRole, setSelectedRole] = useState<string | number | undefined>(
    currentUser?.placeOrderRole?.placeOrderRoleId ?? ''
  );
  const [selectedReportsTo, setSelectedReportsTo] = useState<string | null>(
    currentUser?.reportsTo?.accountName ?? ''
  );
  const [isLoadingRoles, setLoadingRoles] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAvailableReportsTo, setIsLoadingAvailableReportsTo] = useState(false);
  const [availableReportsTo, setAvailableReportsTo] = useState<IManagePlaceOrderReportsTo[]>([]);
  const [roles, setRoles] = useState<IDropdownResponse[]>([]);

  const isDirty =
    selectedRole !== (currentUser?.placeOrderRole?.placeOrderRoleId ?? '') ||
    selectedReportsTo !== (currentUser?.reportsTo?.accountName ?? '');

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const placeOrderRoles = await getAdminLookupPlaceOrderRoles();
      setRoles(alphaSort(placeOrderRoles, 'description'));
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const hasReportsTo: boolean = useMemo(() => {
    if (selectedRole === '') return false;

    return isAllowedToBeEditedOrDeleted(
      roles?.find(role => +role.value === selectedRole)?.description
    );
  }, [roles, selectedRole]);

  const getAvailableReportsTo = async (accountName: string) => {
    try {
      setIsLoadingAvailableReportsTo(true);
      const res = await getPlaceOrderAvailableReportsTo(accountName);
      setAvailableReportsTo(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingAvailableReportsTo(false);
    }
  };

  const handleClose = (shouldReload?: boolean, showWarning = true) => {
    if (isDirty) {
      if (showWarning) {
        const result = window.confirm('You have unsaved changes, are you sure you want to exit?');

        if (result) {
          setSelectedReportsTo('');
          setSelectedRole('');
          onClose(shouldReload ?? false);
          return;
        }
        return;
      }
    }
    onClose(shouldReload ?? false);
  };

  const handleSaveRole = async () => {
    try {
      setIsSaving(true);
      await updateUserRoles({
        entraRoleInformation: {
          entraUserId: currentUser?.entraUserId,
          roleIds: selectedAppRoles.map(val => val.value)
        },
        placeOrderRoleInformation: {
          displayName: currentUser?.displayName,
          accountName: currentUser?.accountName,
          roleId: Number(selectedRole),
          reportsTo: selectedReportsTo
        }
      });
      enqueueSnackbar('User App Roles Updated!', {
        variant: 'success'
      });

      fetchUsers && fetchUsers();
      if (user.userName === currentUser.accountName) loadUser(true);
      handleClose(true, false);
    } catch (error) {
      return enqueueSnackbar(
        error?.Detail ??
          'Error, we were unable to update the user at this time. Please try again later.',
        {
          variant: 'error'
        }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const [isLoadingAccessInformation, setIsLoadingAccessInformation] = useState(false);
  const [userAccessInformation, setUserAccessInformation] = useState<IAccessInformation | null>(
    null
  );
  const getUserAccessInformation = async (accountName: string) => {
    try {
      setIsLoadingAccessInformation(true);
      const res = await getAccessInformation(accountName);
      setUserAccessInformation(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingAccessInformation(false);
    }
  };

  useEffect(() => {
    if (selectedRole && currentUser) {
      getAvailableReportsTo(currentUser.accountName);
    }
    if (currentUser) getUserAccessInformation(currentUser.accountName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const [appRoles, setAppRoles] = useState<IDropdownResponse[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isLoadingAppRoles, setIsLoadingAppRoles] = useState<boolean>(false);

  const getAppRoles = async () => {
    try {
      setIsLoadingAppRoles(true);
      const res = await getAllAppRoles();
      setAppRoles(res);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingAppRoles(false);
    }
  };

  const [isLoadingUserAppRoles, setIsLoadingUserAppRoles] = useState<boolean>(true);
  const [selectedAppRoles, setSelectedAppRoles] = useState<IDropdownResponse[]>([]);
  const getCurrentUserAppRoles = async () => {
    try {
      setIsLoadingUserAppRoles(true);
      const appRoles = await getAllAppRoles();
      setAppRoles(appRoles);
      const res = await getUserAppRoles(currentUser?.entraUserId);
      setSelectedAppRoles(
        res.roles
          .map((val: string) => appRoles.find(role => role.value === val))
          .filter((role): role is IDropdownResponse => role !== undefined)
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingUserAppRoles(false);
    }
  };

  useEffect(() => {
    if (currentUser?.entraUserId) {
      getCurrentUserAppRoles();
    } else {
      getAppRoles();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleClose(false, isDirty);
        }}
        maxWidth="sm"
      >
        <Fade in={open}>
          <Box mt={1}>
            {(isLoadingRoles ||
              isSaving ||
              isLoadingAvailableReportsTo ||
              isLoadingAccessInformation) && <Loader position="centered" type="overlay" />}
            <Box marginBottom="0.5rem" display="flex" flexWrap="wrap">
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5">Edit {currentUser?.displayName}</Typography>
              </Box>
            </Box>
            <Divider />
            <Grid container spacing={1} sx={{ marginTop: '0.5rem' }}>
              <Grid item xs={12} sm={hasReportsTo ? 6 : 12}>
                <SMBSelect
                  label="Role"
                  handleClear={() => {
                    setSelectedRole('');
                    setSelectedReportsTo('');
                  }}
                  value={selectedRole}
                  disabled={isLoadingRoles}
                  onChange={event => {
                    setSelectedRole(Number(event.target.value));
                    if (!Boolean(event.target.value)) {
                      setSelectedReportsTo('');
                    }
                  }}
                >
                  {roles?.map((role, index) => {
                    return (
                      <MenuItem key={`${index}`} value={role.value}>
                        {role.description}
                      </MenuItem>
                    );
                  })}
                </SMBSelect>
              </Grid>
              <Grid item xs={12} sm={hasReportsTo ? 6 : 12}>
                {hasReportsTo && (
                  <SMBSelect
                    disabled={isLoadingAvailableReportsTo}
                    label="Reports to"
                    required={!hasReportsTo}
                    handleClear={() => setSelectedReportsTo('')}
                    value={selectedReportsTo}
                    onChange={event => {
                      setSelectedReportsTo(event.target.value);
                    }}
                  >
                    {availableReportsTo?.map((reportsTo, index) => {
                      return (
                        <MenuItem key={`${index}`} value={reportsTo.accountName}>
                          {reportsTo.displayName}
                        </MenuItem>
                      );
                    })}
                  </SMBSelect>
                )}
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  disabled={isLoadingAppRoles || isLoadingUserAppRoles}
                  id="Security Roles"
                  value={selectedAppRoles}
                  onChange={(event, newValue: IDropdownResponse[]) => {
                    setSelectedAppRoles(newValue);
                  }}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  options={appRoles}
                  getOptionLabel={option => option?.shorthand}
                  filterSelectedOptions
                  renderOption={(props, option: IDropdownResponse) => {
                    return (
                      <li {...props} key={`${option?.value}`}>
                        {option?.shorthand}
                      </li>
                    );
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      disabled={isLoadingAppRoles}
                      label={isLoadingAppRoles ? 'Loading...' : 'App Roles'}
                      placeholder="Add App Roles"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                {userAccessInformation?.territories && (
                  <AccordionList
                    pluralItemName={'Territories'}
                    title={`Territories (${
                      Array.from(
                        new Set(
                          userAccessInformation?.territories.flatMap(
                            territory => territory.territoryNames
                          )
                        )
                      ).length
                    })`}
                    items={Array.from(
                      new Set(
                        userAccessInformation?.territories.flatMap(
                          territory => territory.territoryNames
                        )
                      )
                    )?.sort((a, b) => a?.localeCompare(b))}
                  />
                )}
                {userAccessInformation?.neighborhoods && (
                  <AccordionList
                    pluralItemName={'Neighborhoods'}
                    title={`Neighborhoods (${
                      Array.from(
                        new Set(
                          userAccessInformation?.neighborhoods.flatMap(
                            neighborhood => neighborhood.neighborHoods
                          )
                        )
                      ).length
                    })`}
                    items={Array.from(
                      new Set(
                        userAccessInformation?.neighborhoods.flatMap(
                          neighborhood => neighborhood.neighborHoods
                        )
                      )
                    )?.sort((a, b) => a?.localeCompare(b))}
                  />
                )}
                {userAccessInformation?.jobs && (
                  <AccordionList
                    pluralItemName={'Job Assignments'}
                    title={`Job Assignments (${
                      Array.from(
                        new Set(
                          userAccessInformation?.jobs
                            .flatMap(job => job.jobs)
                            .map(job => `${job.neighborhoodName}-${job.lotNumber}`)
                        )
                      ).length
                    })`}
                    items={Array.from(
                      new Set(
                        userAccessInformation?.jobs
                          .flatMap(job => job.jobs)
                          .map(job => `${job.neighborhoodName}-${job.lotNumber}`)
                      )
                    )?.sort((a, b) => a?.localeCompare(b))}
                  />
                )}
              </Grid>
            </Grid>
            <Box display="flex" alignItems="center" mt={1}>
              <Box flex={1}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isSaving}
                  startIcon={<Save />}
                  onClick={handleSaveRole}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};
